import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { ContentState, EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { removeDuplicationFromArray } from 'utilitys/helpers/general';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { ClearButton } from 'components/common/richTextArea/components/ClearButton/clearButton';
import { CharsCounter } from 'components/common/richTextArea/components/charsCounter/CharsCounter';

import './customToolBar.css';
import classes from './richTextArea.module.scss';

// Avoid spamming `alert` if the user holds a key at the limit.
const CHARACTER_LIMIT_ALERT_THROTTLE_MILLISECONDS = 2500;

type Props = {
    onEditorDescription: (data: any, mentionedList: string[]) => void;
    existingData: string;
    editorContentHeight?: number;
    isDisable?: boolean;
    isAutoFocus?: boolean;
    isError?: boolean;
    mention?: {
        separator: string;
        trigger: string;
        suggestions: { text: string; value: string; url: string }[];
    };
    id?: string;
    placeholder?: string;
    isClearButton?: boolean;
    customToolbarButtons?: ReactNode[];
    isLoading?: boolean;
    loadingText?: string;
    editorTextClassName?: string;
    showCounter?: boolean;
    maxCharsCount?: number;
};

const RichTextArea = React.forwardRef(
    (
        {
            onEditorDescription,
            existingData = '',
            editorContentHeight = 0,
            isDisable = false,
            isAutoFocus = false,
            isError = false,
            isClearButton = false,
            customToolbarButtons,
            isLoading = false,
            loadingText = undefined,
            editorTextClassName,
            showCounter = false,
            maxCharsCount = 1000,
            ...rest
        }: Props,
        ref: React.LegacyRef<HTMLDivElement>,
    ) => {
        const { handleSnackBar } = useCustomSnackBar();
        const editorRef = useRef<any>(null);
        const lastCharacterLimitAlertTimestampReference = useRef(0);

        // Alert when maxCharsCount cap is reached (throttled).
        const onAlertMaxCharsLimitReached = () => {
            const now = Date.now();
            if (
                now - lastCharacterLimitAlertTimestampReference.current >=
                CHARACTER_LIMIT_ALERT_THROTTLE_MILLISECONDS
            ) {
                lastCharacterLimitAlertTimestampReference.current = now;
                handleSnackBar(
                    `You have reached the maximum length of ${maxCharsCount.toLocaleString()} characters.`,
                    'error',
                );
            }
        };

        useEffect(() => {
            if (isAutoFocus && editorRef && editorRef.current) {
                editorRef.current.focusEditor();
            }
        }, []);

        const [editorDescription, setEditorDescription] = useState(() => {
            if (existingData && existingData.length > 0) {
                const blocksFromHtml = htmlToDraft(existingData);
                const { contentBlocks, entityMap } = blocksFromHtml;
                const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                const editorStateTemp = EditorState.createWithContent(contentState);
                return isAutoFocus ? EditorState.moveFocusToEnd(editorStateTemp) : editorStateTemp;
            }
            return EditorState.createEmpty();
        });

        //Last editor state at or under the limit; used to revert illegal updates (e.g. Enter adds a block → extra `\n`).
        const lastValidEditorStateReference = useRef(editorDescription);

        const setDescription = (desc) => {
            const blocksFromHtml = htmlToDraft(desc);
            const { contentBlocks, entityMap } = blocksFromHtml;
            const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
            const editorStateTemp = EditorState.createWithContent(contentState);

            const stateToSave = isAutoFocus ? EditorState.moveFocusToEnd(editorStateTemp) : editorStateTemp;
            setEditorDescription(stateToSave);
            // Keep revert baseline in sync when `existingData` replaces the editor from outside.
            lastValidEditorStateReference.current = stateToSave;
        };

        useEffect(() => {
            const currentContent = editorDescription.getCurrentContent();
            const currentHtml = draftToHtml(convertToRaw(currentContent));

            if (existingData?.length > 0 && existingData !== currentHtml) {
                setDescription(existingData);
            }
        }, [existingData]);

        const getMentionUsers = (obj) => {
            const usersArr = [] as any;
            for (const value of Object.values(obj) as any) {
                if (value.type === 'MENTION') {
                    let userLengthAfterSplit = value.data.url.split('/').length;
                    let user =
                        userLengthAfterSplit.length === 1
                            ? value.data.url.split('/')[0]
                            : value.data.url.split('/')[userLengthAfterSplit - 1];
                    usersArr.push(user);
                }
            }
            return removeDuplicationFromArray(usersArr);
        };

        const handleEditorDescription = (editorState) => {
            // Catch-all cap: Enter / new paragraph is not `handleBeforeInput`; splitting blocks increases `getPlainText('\n')` length.
            const nextPlainTextLength = editorState.getCurrentContent().getPlainText('\n').length;
            const previousPlainTextLength = lastValidEditorStateReference.current
                .getCurrentContent()
                .getPlainText('\n').length;

            // Revert only when we cross the cap from a legal length (skip loop if content was already over limit from server).
            if (nextPlainTextLength > maxCharsCount && previousPlainTextLength <= maxCharsCount) {
                onAlertMaxCharsLimitReached();
                setEditorDescription(lastValidEditorStateReference.current);
                return;
            }

            lastValidEditorStateReference.current = editorState;
            setEditorDescription(editorState);

            const mentionedList = getMentionUsers(convertToRaw(editorState.getCurrentContent()).entityMap);

            onEditorDescription(draftToHtml(convertToRaw(editorState.getCurrentContent())), mentionedList);
        };

        const handleEditorLength = () => {
            const currentContent = editorDescription.getCurrentContent();
            const currentContentLength = currentContent.getPlainText('\n').length;
            const selection = editorDescription.getSelection();

            if (selection.isCollapsed() && currentContentLength >= maxCharsCount) {
                onAlertMaxCharsLimitReached();
                return 'handled';
            }
        };

        const handleEditorPastedText = (pastedText) => {
            const currentContent = editorDescription.getCurrentContent();
            const currentContentLength = currentContent.getPlainText('\n').length;

            if (currentContentLength + pastedText.length > maxCharsCount) {
                onAlertMaxCharsLimitReached();
                return 'handled';
            }
        };

        const plainTextCharacterCount = editorDescription.getCurrentContent().getPlainText('\n').length;

        const containerHeight = {
            height: editorContentHeight || '',
        };

        return (
            <div className={`${classes.editorTextAreaContainer} custom-toolbar`} style={containerHeight} ref={ref}>
                <Editor
                    editorState={editorDescription}
                    editorClassName={`${classes.editorTextArea} ${isError && classes.error} ${editorTextClassName || ''}`}
                    onEditorStateChange={handleEditorDescription}
                    handleBeforeInput={handleEditorLength}
                    handlePastedText={handleEditorPastedText}
                    readOnly={isDisable}
                    ref={editorRef}
                    toolbarCustomButtons={[
                        ...(isClearButton
                            ? [
                                  <ClearButton
                                      onClear={() => {
                                          const emptyEditorState = EditorState.createEmpty();
                                          lastValidEditorStateReference.current = emptyEditorState;
                                          setEditorDescription(emptyEditorState);
                                          onEditorDescription('', []);
                                      }}
                                      disabled={isDisable}
                                  />,
                              ]
                            : []),
                        ...(showCounter
                            ? [<CharsCounter max={maxCharsCount} current={plainTextCharacterCount} />]
                            : []),
                        ...(customToolbarButtons ? customToolbarButtons : []),
                    ]}
                    toolbar={{
                        image: {
                            urlEnabled: true,
                            alt: { present: true, mandatory: false },
                            alignmentEnabled: true,
                            defaultSize: 'auto',
                        },
                    }}
                    {...rest}
                />
                {isLoading && (
                    <div className={classes.loaderOverlay}>
                        <div className={classes.loaderContent}>
                            <div className={classes.spinner} aria-label={'Loading'} />
                            <div className={classes.processingText}>{loadingText ? loadingText : 'Loading...'}</div>
                        </div>
                    </div>
                )}
            </div>
        );
    },
);

export default RichTextArea;
